import { useEffect, useState } from 'react'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { printApi, type LocalAgentConfig } from '@/lib/api/print'
import { useToast } from '@/hooks/useToast'

interface ScanAgentConfigPanelProps {
  taskUuid: string
  boxNum: number | null
  workerName: string
}

export function ScanAgentConfigPanel({ taskUuid, boxNum, workerName }: ScanAgentConfigPanelProps) {
  const { toast } = useToast()
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [ports, setPorts] = useState<string[]>([])
  const [cfg, setCfg] = useState<LocalAgentConfig>({
    server_url: 'http://127.0.0.1:8080',
    task_uuid: '',
    box_num: 1,
    worker_name: '',
    scan_port: 'COM3',
    scan_baud: 115200,
    agent_token: '',
  })

  useEffect(() => {
    if (!open) return
    void (async () => {
      const [c, p] = await Promise.all([printApi.getLocalAgentConfig(), printApi.listSerialPorts()])
      if (c) setCfg(c)
      if (p?.ports) setPorts(p.ports)
    })()
  }, [open])

  useEffect(() => {
    if (boxNum == null) return
    void printApi.putLocalAgentConfig({
      task_uuid: taskUuid,
      box_num: boxNum,
      worker_name: workerName,
    })
  }, [taskUuid, boxNum, workerName])

  const handleSave = async () => {
    setLoading(true)
    try {
      const res = await printApi.putLocalAgentConfig(cfg)
      if (!res?.ok) throw new Error('агент не ответил')
      toast.success('Настройки локального агента сохранены')
    } catch {
      toast.error('Не удалось сохранить — запущен ли print-agent?')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="mb-4 border border-gray-200 rounded-lg bg-gray-50">
      <button
        type="button"
        className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-gray-700"
        onClick={() => setOpen(v => !v)}
      >
        Локальный агент — сканер (COM)
        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3 border-t border-gray-200 pt-3">
          <p className="text-xs text-gray-500">
            Один процесс <code className="bg-white px-1 rounded">print-agent</code> на :8888 — печать и скан.
            Токен должен совпадать с <code className="bg-white px-1 rounded">SCAN_AGENT_TOKEN</code> на сервере,
            но вводится и сохраняется отдельно в локальном <code className="bg-white px-1 rounded">print-agent</code>.
          </p>
          <div>
            <label className="text-xs font-medium text-gray-600">Адрес сервера</label>
            <Input
              value={cfg.server_url}
              onChange={e => setCfg(c => ({ ...c, server_url: e.target.value }))}
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600">Токен агента</label>
            <Input
              type="password"
              value={cfg.agent_token || ''}
              onChange={e => setCfg(c => ({ ...c, agent_token: e.target.value }))}
              placeholder="dev-scan-token"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-600">UUID задания</label>
              <Input
                value={cfg.task_uuid || ''}
                onChange={e => setCfg(c => ({ ...c, task_uuid: e.target.value }))}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600">Коробка №</label>
              <Input
                type="number"
                min={1}
                value={cfg.box_num || 1}
                onChange={e => setCfg(c => ({ ...c, box_num: Number(e.target.value) || 1 }))}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-600">COM-порт</label>
              <select
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                value={cfg.scan_port || ''}
                onChange={e => setCfg(c => ({ ...c, scan_port: e.target.value }))}
              >
                {(ports.length ? ports : [cfg.scan_port || 'COM3']).map(p => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600">Baud</label>
              <Input
                type="number"
                value={cfg.scan_baud || 115200}
                onChange={e => setCfg(c => ({ ...c, scan_baud: Number(e.target.value) || 115200 }))}
              />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600">Оператор</label>
            <Input
              value={cfg.worker_name || ''}
              onChange={e => setCfg(c => ({ ...c, worker_name: e.target.value }))}
            />
          </div>
          <Button size="sm" onClick={() => void handleSave()} isLoading={loading}>
            Сохранить в агент
          </Button>
        </div>
      )}
    </div>
  )
}
